import { FileOpen, Delete, Email } from "@mui/icons-material";
import { Button, Dialog, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import {
  SignerType,
  TravelDocument,
  TravelDocumentOperationType,
} from "../../common/TravelDocument";
import {
  useDeleteTrainingTravelDocumentMutation,
  useDownloadTrainingTravelDocumentMutation,
  useGetTrainingTravelDocumentsQuery,
} from "../../redux/api/travelDocument";
import EmailModal from "../email/EmailModal";
import TrainingTravelDocumentsUploadModal from "./TrainingTravelDocumentsUploadModal";
import { toast } from "react-toastify";
import { EmailType } from "../../common/EmailDetails";

interface TrainingTravelDocumentsProps {
  isViewOnly: boolean;
  trainingOrderId: number;
  signer: SignerType;
  emailTo: string | undefined;
  validEmail: boolean | undefined;
  parentCallback: (
    signer: SignerType,
    operation: TravelDocumentOperationType
  ) => void;
}

const TrainingTravelDocuments = (props: TrainingTravelDocumentsProps) => {
  const [travelDocuments, setTravelDocuments] = useState<TravelDocument[]>();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [currDocId, setCurrDocId] = useState<number>(); // used to keep track of which document the confirm dialog is referencing

  const {
    data: documentsFromApi,
    isLoading,
    isFetching,
    refetch,
  } = useGetTrainingTravelDocumentsQuery(props.trainingOrderId.toString());

  const [deleteTravelDocument] = useDeleteTrainingTravelDocumentMutation();
  const [downloadTravelDocument] = useDownloadTrainingTravelDocumentMutation();

  useEffect(() => {
    if (!isFetching && !isLoading) {
      let res: any = JSON.parse(JSON.stringify(documentsFromApi));
      setTravelDocuments(
        res.map((doc: any) => {
          // convert null values to empty string
          Object.keys(doc).forEach(
            (key) => (doc[key] = doc[key] === null ? "" : doc[key])
          );
          return doc as TravelDocument;
        })
      );
    }
  }, [documentsFromApi, isFetching, isLoading]);

  const handleUpload = () => {
    refetch();
    props.parentCallback(props.signer, TravelDocumentOperationType.UPLOAD);
  };

  // const handleDownload = async (url: string, fileName: string) => {
  //   const response = await downloadTravelDocument({
  //     trainingOrderId: props.trainingOrderId.toString(),
  //     fileKey: url,
  //   });
  //   if ("error" in response === false) {
  //     // create a temporary URL and link and simulate a click to trigger the browser download behavior
  //     // @ts-ignore - if there is no error object in the response, we know it must have a Blob 'data' attribute
  //     const url = window.URL.createObjectURL(response.data);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", fileName);
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);

  //     props.parentCallback(props.signer, TravelDocumentOperationType.DOWNLOAD);
  //   }
  // };

  const handleView = async (key: string, contentType: string, fileName: string) => {
    const response = await downloadTravelDocument({
      trainingOrderId: props.trainingOrderId.toString(),
      fileKey: key,
    });
    if ("error" in response === false) {
      // @ts-ignore - if there is no error object in the response, we know it must have a Blob 'data' attribute
      const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
      const inlineTypes = [
        'application/pdf',
        'image/png',
        'image/jpeg',
      ];

      if (inlineTypes.includes(contentType)) {
        window.open(url, "_blank");
      } else {
        const link = document.createElement("a");
        
        link.href = url;
        link.target = "_blank"
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleOpen = (documentId: number) => {
    setCurrDocId(documentId);
    setConfirmDelete(true);
  };

  const handleClose = () => {
    setConfirmDelete(false);
  };

  const deleteDocument = async () => {
    if (currDocId === undefined) {
      return;
    }
    const response = await deleteTravelDocument({
      trainingOrderId: props.trainingOrderId.toString(),
      travelDocumentId: currDocId.toString(),
    });
    if ("error" in response === false) {
      refetch();
      props.parentCallback(props.signer, TravelDocumentOperationType.DELETE);
      handleClose();
    }
  };

  const handleEmailCallBack = () => {
    toast.success(<p>Email sent successfully.</p>, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "colored",
    });
  };

  return (
    <>
      <ul className="upload-list">
        {travelDocuments
          ?.filter((doc) => doc.signerType === props.signer)
          .map((doc) => (
            <li key={doc.id} className="document-item">
              <span>{doc.displayName}</span>
              {/* TODO: Sent on {date} */}
              {!props.isViewOnly && (
                <div className="document-actions">
                  <span>
                    <Button
                      className="blue-link-button"
                      onClick={() => handleOpen(doc.id)}
                    >
                      <Delete />
                      Delete
                    </Button>
                  </span>
                  {!!props.validEmail && !!props?.emailTo ? (
                    <EmailModal
                      key={EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER}
                      email={props?.emailTo}
                      emailType={EmailType.EMAIL_TRAINING_DOCUMENT_TO_MARINER}
                      uploadedFileUrl={doc.fileUrl.split('/').pop()}
                      uploadedFileName={doc.displayName}
                      trainingOrderId={props.trainingOrderId}
                      parentCallback={handleEmailCallBack}
                    >
                      <Button className="blue-link-button">
                        <Email />
                        Send to Mariner
                      </Button>
                    </EmailModal>
                  ) : (
                    <Button className="grey-link-button">
                      <Email />
                      Send to Mariner
                    </Button>
                  )}
                  <span>
                    <Button
                      className="blue-link-button"
                      onClick={() => {
                        handleView(doc.fileKey, doc.contentType, doc.displayName);
                      }}
                    >
                      <FileOpen />
                      View
                    </Button>
                    {/* <Button
                      onClick={() => handleDownload(doc.fileUrl, doc.displayName)}
                      className="blue-link-button"
                    >
                      <CloudDownload />
                      Download
                    </Button> */}
                  </span>
                </div>
              )}
            </li>
          ))}
      </ul>
      {!props.isViewOnly && (
        <TrainingTravelDocumentsUploadModal
          trainingOrderId={props.trainingOrderId}
          signer={props.signer}
          onUpload={handleUpload}
        />
      )}
      <Dialog
        open={confirmDelete}
        onClose={handleClose}
        aria-labelledby="modal-title"
      >
        <Typography component="h1" id="modal-title">
          Delete Document
        </Typography>
        <p>Are you sure you would like to delete this document?</p>
        <div className="modal-footer">
          <Button className="outline-button-small" onClick={deleteDocument}>
            Confirm
          </Button>
          <Button className="blue-button-small" onClick={handleClose}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </>
  );
};

export default TrainingTravelDocuments;
